import { FC, useEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { County } from "@app/geography";
import { RegionSet } from "@app/region-sets";
import RegionSetService from "@app/region-sets/regionSetService";
import { ProjectRunConfigurationType, RegionSetStatusType } from "@app/projects/enumerators";
import ServiceAreaContextProvider from "@app/region-sets/editor/ServiceAreaEditorContext";
import { ResourceType } from "@app/resources";
import ProjectService from "@app/projects/projectService";
import Project from "@app/projects/project";
import TemplateService from "@app/templates/templateService";
import { EditRegionSetInner } from "@app/projects/edit/EditRegionSetInner";
import { CountyStatus } from "@app/projects/create/countyStatus";

const EditRegionSet: FC = () => {
  const projectService = useRef(new ProjectService()).current;
  const regionSetService = useRef(new RegionSetService()).current;
  const templateService = useRef(new TemplateService()).current;
  const routeMatch = useRouteMatch<{ projectId: string }>();

  const [project, setProject] = useState<Project | null>(null);
  const [regionSet, setRegionSet] = useState<RegionSet | null>(null);
  const [countyStatuses, setCountyStatuses] = useState<CountyStatus[]>([]);
  const [updateCounties, setUpdateCounties] = useState<County[]>([]);
  const [usesPlanningRegionTemplate, setUsesPlanningRegionTemplate] = useState(false);

  useEffect(() => {
    const projectId = Number(routeMatch.params.projectId);
    if (projectId) {
      projectService.getById(projectId).then((newProject) => {
        const newIsZipProject = newProject.resource.resourceType === ResourceType.ZipAnalysisProject;
        // Folded into the same Promise.all as the region set below so both land in the same state
        // batch - ServiceAreaContextProvider must never mount with a stale default usesPlanningRegion,
        // since its child's county-fetch effect only runs once, on mount.
        const usesPlanningRegionPromise = newProject.adequacyTemplateCollectionId && newProject.adequacyTemplateCorrelationId ?
          templateService.getLatestTemplateByCorrelationId(newProject.adequacyTemplateCollectionId, newProject.adequacyTemplateCorrelationId, false)
            .then((templateMetadata) => templateMetadata.usesPlanningRegion)
            .catch(() => false) :
          Promise.resolve(false);

        const promises: Promise<RegionSet | CountyStatus[] | boolean>[] = [
          regionSetService.getById(newProject.serviceAreaRegionSetId),
          getCountyStatuses(newProject, newIsZipProject),
          usesPlanningRegionPromise,
        ];
        Promise.all(promises).then(([newRegionSet, newCountyStatuses, usesPlanningRegion]) => {
          setProject(newProject);
          setUsesPlanningRegionTemplate(usesPlanningRegion as boolean);
          setRegionSet(newRegionSet as RegionSet);
          setCountyStatuses(newCountyStatuses as CountyStatus[]);
        })
      });
    }
  }, []);

  const getCountyStatuses = async (project: Project, isZipTemplate: boolean) => {
    const statuses = isZipTemplate ? [] : await projectService.getCountyStatusInfo(
      project.projectId,
      project.serviceAreaRegionSetId,
      await projectService.maxRunConfigurationId(project, ProjectRunConfigurationType.DualMembershipPrimary),
    );

    return statuses;
  }

  const handleSubmitNearbyCounties = (newUpdateCounties: County[], regionStatus: RegionSetStatusType) => {
    const statuses = newUpdateCounties.map((county) =>
    ({
      countySSA: county.countySSA,
      countyZips: [],
      status: regionStatus,
    } as CountyStatus),
    );

    setCountyStatuses(getUpdatedCountyStatuses(statuses));
    setUpdateCounties(newUpdateCounties);
  }

  const getUpdatedCountyStatuses = (statuses: CountyStatus[]) => {
    const updatedStatus = structuredClone(countyStatuses);
    for (const status of statuses) {
      const indexToReplace = updatedStatus.findIndex((item) => item.countySSA === status.countySSA);
      if (indexToReplace >= 0) {
        if (status.status === RegionSetStatusType.Unknown) {
          updatedStatus.splice(indexToReplace, 1);
        } else {
          updatedStatus[indexToReplace] = status;
        }
      } else {
        if (status.status !== RegionSetStatusType.Unknown) {
          updatedStatus.push(status);
        }
      }
    }

    return updatedStatus;
  }

  const projectName = !!project?.resource ? project.resource.name : "Loading...";

  if (!regionSet) {
    return null;
  }

  return (
    <ServiceAreaContextProvider
      countyStatuses={countyStatuses}
      isZipProject={project?.resource.resourceType === ResourceType.ZipAnalysisProject}
      projectName={projectName}
      regionSet={regionSet}
      usesPlanningRegion={usesPlanningRegionTemplate}
    >
      <EditRegionSetInner
        countyStatuses={countyStatuses}
        getUpdatedCountyStatuses={getUpdatedCountyStatuses}
        handleSubmitNearbyCounties={handleSubmitNearbyCounties}
        project={project}
        regionSet={regionSet}
        setCountyStatuses={setCountyStatuses}
        setUpdateCounties={setUpdateCounties}
        updateCounties={updateCounties}
      />
    </ServiceAreaContextProvider>
  );
};
export default EditRegionSet;
